<template>
  <div class="verification-callback-container">
    <div class="verification-card">
      <!-- Loading state -->
      <div v-if="verifying" class="verification-state">
        <div class="spinner"></div>
        <h1>Vérification en cours...</h1>
        <p>Veuillez patienter pendant que nous vérifions votre email</p>
      </div>

      <!-- Success state -->
      <div v-else-if="verified" class="verification-state success">
        <div class="icon-container">
          <div class="success-icon">✓</div>
        </div>
        <h1>Email vérifié !</h1>
        <p>Votre email a été vérifié avec succès.</p>
        <p class="success-instruction">Cet onglet va se fermer automatiquement dans <strong>{{ countdown }}</strong> seconde{{ countdown > 1 ? 's' : '' }}...</p>
        <div class="success-details">
          <p>✅ Votre compte est maintenant activé</p>
          <p>🔐 Vous êtes connecté automatiquement</p>
          <p>👈 Retournez à l'onglet d'inscription pour continuer</p>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="verification-state error">
        <div class="icon-container">
          <div class="error-icon">✗</div>
        </div>
        <h1>Erreur de vérification</h1>
        <p class="error-message">{{ errorMessage }}</p>
        <div class="error-actions">
          <button class="btn btn-secondary" @click="goToOnboarding">
            Retour à l'inscription
          </button>
          <button class="btn btn-primary" @click="goToLogin">
            Aller à la connexion
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService'

export default {
  name: 'EmailVerification',
  data() {
    return {
      verifying: true,
      verified: false,
      error: false,
      errorMessage: '',
      countdown: 3,
    }
  },
  async mounted() {
    // Récupérer le code de vérification depuis l'URL
    const code = this.$route.query.code

    if (!code) {
      this.verifying = false
      this.error = true
      this.errorMessage = 'Code de vérification manquant dans l\'URL.'
      return
    }

    await this.verifyEmail(code)
  },
  methods: {
    async verifyEmail(code) {
      try {
        // Appeler l'API de vérification avec auto-login
        const response = await AuthService.verifyEmail(code)

        console.log('Vérification réussie:', response)

        // Vérifier le rôle de l'utilisateur
        const authStatus = await AuthService.isAuthenticated()

        // Les role_id des accompagnateurs : Enseignant(10), Psychologue(11), Famille(12)
        const accompagnateurRoleIds = [10, 11, 12]

        // Convertir le role en nombre (au cas où ce serait une string)
        const userRoleId = parseInt(authStatus.role)

        console.log('Debug - authStatus.role:', authStatus.role, 'type:', typeof authStatus.role)
        console.log('Debug - userRoleId:', userRoleId, 'type:', typeof userRoleId)
        console.log('Debug - is accompagnateur?', accompagnateurRoleIds.includes(userRoleId))

        // Mapping des role_id vers les noms pour affichage
        const roleNames = {
          1: 'Super Admin',
          2: 'Admin',
          3: 'Moderator',
          4: 'Accompagnateur',
          5: 'Expert',
          6: 'Employer',
          7: 'Parent',
          8: 'Young Person',
          9: 'Guest',
          10: 'Enseignant',
          11: 'Psychologue',
          12: 'Famille'
        }

        if (authStatus.authenticated && !accompagnateurRoleIds.includes(userRoleId)) {
          // Le compte n'est pas un accompagnateur
          this.verifying = false
          this.error = true
          const roleName = roleNames[userRoleId] || `ID ${userRoleId}`
          this.errorMessage = `Ce compte est de type "${roleName}" et non un compte accompagnateur. Veuillez créer un compte avec un rôle accompagnateur (Enseignant, Psychologue ou Famille).`
          return
        }

        this.verifying = false
        this.verified = true

        // Démarrer le compte à rebours
        this.startCountdown()
      } catch (error) {
        console.error('Erreur de vérification:', error)

        this.verifying = false
        this.error = true

        // Extraire le message d'erreur
        if (error.response?.data?.detail) {
          this.errorMessage = error.response.data.detail
        } else if (error.message) {
          this.errorMessage = error.message
        } else {
          this.errorMessage = 'Une erreur inattendue s\'est produite lors de la vérification.'
        }
      }
    },

    goToOnboarding() {
      this.$router.push('/accompagnant/onboarding')
    },

    goToLogin() {
      this.$router.push('/accompagnant/login')
    },

    startCountdown() {
      const interval = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(interval)
          // Tenter de fermer l'onglet
          window.close()
          // Si la fermeture échoue (onglet ouvert manuellement), afficher un message
          setTimeout(() => {
            if (!window.closed) {
              alert('Veuillez fermer cet onglet et retourner à la page d\'inscription.')
            }
          }, 500)
        }
      }, 1000)
    },
  },
}
</script>

<style scoped>
.verification-callback-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.verification-card {
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.5s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.verification-state {
  text-align: center;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #FF9800;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.success-icon,
.error-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  font-weight: bold;
  animation: popIn 0.5s ease;
}

.success-icon {
  background: #FF9800;
  color: white;
}

.error-icon {
  background: linear-gradient(135deg, #f44336 0%, #e57373 100%);
  color: white;
}

@keyframes popIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

h1 {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 12px;
}

p {
  font-size: 16px;
  color: #546e7a;
  line-height: 1.6;
  margin-bottom: 12px;
}

.success-instruction {
  font-weight: 600;
  color: #FF9800;
  margin-top: 20px;
  font-size: 18px;
}

.success-details {
  margin-top: 24px;
  padding: 20px;
  background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
  border-radius: 12px;
  border-left: 4px solid #FF9800;
}

.success-details p {
  margin: 12px 0;
  font-size: 15px;
  color: #2c3e50;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-message {
  color: #f44336;
  font-weight: 500;
  padding: 12px;
  background: #ffebee;
  border-radius: 8px;
  margin: 16px 0;
}

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: #FF9800;
  color: white;
}

.btn-secondary {
  background: white;
  color: #2c3e50;
  border: 2px solid #e0e7ee;
}

@media (max-width: 600px) {
  .verification-card {
    padding: 30px 20px;
  }

  .error-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>
